import { z } from 'zod';

/**
 * Pure-JS, isomorphic SHA-256 → lowercase hex.
 *
 * Why this exists: `@rynora/contracts/src/index.ts` re-exports
 * everything from this module, so the file is bundled into
 * every frontend app (flow-builder, admin-*, landing-page, …)
 * even though only the backend worker actually calls
 * `computeFingerprint`. A top-level `import { createHash } from
 * 'crypto'` makes Vite externalise the Node built-in and the
 * resulting stub throws on property access at module init
 * ("Cannot access crypto.createHash in client code").
 *
 * The Web Crypto API would solve this in principle, but its
 * `subtle.digest` is async — switching would force every caller
 * (including the unit-test file) to await. Inlining a small
 * sync implementation keeps the public API sync and the impl
 * deterministic across Node and browser.
 *
 * The implementation follows the FIPS 180-4 reference. Output
 * matches `crypto.createHash('sha256').update(input).digest('hex')`
 * byte-for-byte (covered by the `__tests__/error-fingerprint.test.ts`
 * known-vector cases).
 */
function sha256Hex(input: string): string {
  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98,
    0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
    0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8,
    0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819,
    0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
    0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
    0xc67178f2,
  ];
  // UTF-8 encode without depending on TextEncoder (older
  // jsdom-based vitest envs miss it). Manual encoder is small
  // and fully spec-compliant for BMP + supplementary planes.
  const bytes: number[] = [];
  for (let i = 0; i < input.length; i++) {
    let cp = input.charCodeAt(i);
    if (cp >= 0xd800 && cp <= 0xdbff && i + 1 < input.length) {
      const low = input.charCodeAt(i + 1);
      if (low >= 0xdc00 && low <= 0xdfff) {
        cp = ((cp - 0xd800) << 10) + (low - 0xdc00) + 0x10000;
        i++;
      }
    }
    if (cp < 0x80) bytes.push(cp);
    else if (cp < 0x800) bytes.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f));
    else if (cp < 0x10000) bytes.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
    else bytes.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 0x3f), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
  }
  const bitLen = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  // Append 64-bit big-endian bit length.
  for (let i = 7; i >= 0; i--) bytes.push((bitLen / Math.pow(2, i * 8)) & 0xff);

  const H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const W = new Array<number>(64);
  const ROTR = (x: number, n: number) => (x >>> n) | (x << (32 - n));

  for (let block = 0; block < bytes.length; block += 64) {
    for (let i = 0; i < 16; i++) {
      W[i] =
        (bytes[block + i * 4] << 24) |
        (bytes[block + i * 4 + 1] << 16) |
        (bytes[block + i * 4 + 2] << 8) |
        bytes[block + i * 4 + 3];
      W[i] >>>= 0;
    }
    for (let i = 16; i < 64; i++) {
      const s0 = ROTR(W[i - 15], 7) ^ ROTR(W[i - 15], 18) ^ (W[i - 15] >>> 3);
      const s1 = ROTR(W[i - 2], 17) ^ ROTR(W[i - 2], 19) ^ (W[i - 2] >>> 10);
      W[i] = (W[i - 16] + s0 + W[i - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, h] = H;
    for (let i = 0; i < 64; i++) {
      const S1 = ROTR(e, 6) ^ ROTR(e, 11) ^ ROTR(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i] + W[i]) >>> 0;
      const S0 = ROTR(a, 2) ^ ROTR(a, 13) ^ ROTR(a, 22);
      const mj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + mj) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    H[0] = (H[0] + a) >>> 0;
    H[1] = (H[1] + b) >>> 0;
    H[2] = (H[2] + c) >>> 0;
    H[3] = (H[3] + d) >>> 0;
    H[4] = (H[4] + e) >>> 0;
    H[5] = (H[5] + f) >>> 0;
    H[6] = (H[6] + g) >>> 0;
    H[7] = (H[7] + h) >>> 0;
  }
  return H.map((v) => v.toString(16).padStart(8, '0')).join('');
}

/**
 * `pending-test-observability-tier` — pure fingerprint
 * extraction over a failed step's error text.
 *
 * Used by the API worker
 * (`packages/api/src/workers/error-fingerprint-aggregator-worker.ts`),
 * the test-runs ingestion path, and any future CLI / SDK
 * caller that wants to cluster failures off-line.
 *
 * Design choices:
 *   • **Stable hash.** SHA-256 over a normalised
 *     `(message + top-3-stack-frames)`. Two failures with the
 *     same root cause produce the same hash.
 *   • **Normalisation rules.** Strip line/column suffixes, hex
 *     IDs, UUIDs, `0x…` pointers, ISO timestamps, and
 *     ephemeral selectors (text in quotes that look generated).
 *     Lowercase the result. The rule set is documented inline
 *     so an operator triaging a misclustered group can audit
 *     why two errors hashed together.
 *   • **Defensive.** Anything off-shape collapses to an
 *     empty fingerprint (`null`). The caller skips persistence
 *     when `fingerprint_hash === null`.
 *
 * The codebase reality (vs design doc):
 *   • There is no `run_attempts` / `step_executions` table.
 *     The failed-step error lives inside
 *     `test_runs.step_results JSONB[]` — each entry has
 *     `{ status, error?, stepTitle?, stepType?, … }`.
 *   • A single failed run can have N failed steps. The
 *     extractor returns ONE fingerprint per failed step so the
 *     join table can record every distinct failure mode.
 */

export const ERROR_FINGERPRINT_VERSION = 1;

/**
 * Each entry the aggregator walks. Loose-typed because the
 * upstream JSONB is also loose.
 */
export interface RawFailedStep {
  stepId?: string;
  stepTitle?: string;
  stepType?: string;
  status?: string;
  error?: string;
  errorStack?: string;
  /** When the step had a `healingInfo` and ultimately passed. */
  healingInfo?: unknown;
}

export interface FingerprintInput {
  /** Free-form error message lifted from `step.error`. */
  message: string;
  /** Optional stack — split by newline. */
  stack?: string;
  /**
   * The step kind that produced the error. Two different step
   * kinds with the same error message are NOT the same bug —
   * `click failed` on a checkout button is unrelated to
   * `click failed` on a login button at the fingerprint
   * level, so the step kind is hashed in.
   */
  stepType?: string;
}

export interface FingerprintResult {
  /** SHA-256 hex digest, 64 chars. `null` when input was empty. */
  fingerprint_hash: string | null;
  /** Post-normalisation message — what the dashboard renders. */
  normalised_message: string;
  /**
   * Top-3 stack frames after normalisation. Useful for
   * showing the bug in the drawer + as a search field. May be
   * fewer than 3 entries (or 0 if no stack was provided).
   */
  top_stack_frames: string[];
  /**
   * Set when the normaliser had to drop noisy tokens. Operator
   * tools can show "fingerprint built without N volatile
   * tokens" so an operator triages a near-duplicate cluster.
   */
  normalisation_notes: string[];
  /** Schema version so a re-extraction can detect stale rows. */
  version: number;
}

// HEX must contain at least one a-f letter so pure-digit
// epoch timestamps fall through to the epoch / numeric rules.
const HEX_LONG_RE = /\b(?=[a-f0-9]{12,}\b)(?=[a-f0-9]*[a-f])[a-f0-9]+\b/gi;
const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const HEX_POINTER_RE = /\b0x[a-f0-9]{4,}\b/gi;
const LINE_COL_RE = /:\d+:\d+\b/g;
const LINE_ONLY_RE = /:\d+(?=\s|\)|$)/g;
const ISO_TIMESTAMP_RE = /\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?\b/g;
// Numeric / epoch rules use non-digit lookbehind/lookahead
// instead of \b so they fire even when a unit follows
// directly ("30000ms", "1747641234567Z").
const EPOCH_MS_RE = /(?<!\d)1\d{12}(?!\d)/g;
const NUMERIC_RE = /(?<!\d)\d{3,}(?!\d)/g;
const QUOTED_TEXT_RE = /"[^"]{1,80}"/g;
const SQUARE_QUOTED_RE = /'[^']{1,80}'/g;
const URL_QUERY_RE = /\?[^\s)]+/g;

/**
 * Normalise a freeform error message into a fingerprint-ready
 * form. Returns the normalised text + the list of tokens that
 * were stripped (for operator-visible audit).
 */
export function normaliseErrorText(input: string): { text: string; notes: string[] } {
  const notes: string[] = [];
  let text = String(input ?? '').trim();
  if (!text) return { text: '', notes };
  if (UUID_RE.test(text)) notes.push('uuid-stripped');
  text = text.replace(UUID_RE, '<uuid>');
  if (HEX_POINTER_RE.test(text)) notes.push('pointer-stripped');
  text = text.replace(HEX_POINTER_RE, '<ptr>');
  if (HEX_LONG_RE.test(text)) notes.push('hex-id-stripped');
  text = text.replace(HEX_LONG_RE, '<hex>');
  if (ISO_TIMESTAMP_RE.test(text)) notes.push('timestamp-stripped');
  text = text.replace(ISO_TIMESTAMP_RE, '<ts>');
  if (EPOCH_MS_RE.test(text)) notes.push('epoch-stripped');
  text = text.replace(EPOCH_MS_RE, '<epoch>');
  if (LINE_COL_RE.test(text)) notes.push('line-col-stripped');
  text = text.replace(LINE_COL_RE, '');
  if (LINE_ONLY_RE.test(text)) notes.push('line-stripped');
  text = text.replace(LINE_ONLY_RE, '');
  if (URL_QUERY_RE.test(text)) notes.push('querystring-stripped');
  text = text.replace(URL_QUERY_RE, '');
  if (QUOTED_TEXT_RE.test(text)) notes.push('quoted-stripped');
  text = text.replace(QUOTED_TEXT_RE, '"<q>"');
  if (SQUARE_QUOTED_RE.test(text)) notes.push('quoted-stripped');
  text = text.replace(SQUARE_QUOTED_RE, "'<q>'");
  if (NUMERIC_RE.test(text)) notes.push('numeric-stripped');
  text = text.replace(NUMERIC_RE, '<n>');
  text = text.replace(/\s+/g, ' ').trim().toLowerCase();
  // Cap length so a multi-MB stack-dump-as-error doesn't blow
  // up the hash.
  if (text.length > 2000) {
    notes.push('truncated');
    text = text.slice(0, 2000);
  }
  return { text, notes: Array.from(new Set(notes)) };
}

/**
 * Pick the top-N stack frames after normalisation. Returns
 * `[]` when no recognisable frames are present.
 */
export function extractTopStackFrames(stack: string | undefined, n = 3): string[] {
  if (!stack || typeof stack !== 'string') return [];
  const lines = stack
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !/^Error/i.test(l));
  const frames: string[] = [];
  for (const line of lines) {
    // Common forms: "at fnName (path/to/file.ts:12:5)", "at
    // /path:12:5", or just "path/to/file.ts:12". Strip the
    // line/col to keep the frame stable across builds.
    const { text } = normaliseErrorText(line);
    if (text) frames.push(text);
    if (frames.length >= n) break;
  }
  return frames;
}

/**
 * The pure entry point. Returns a `FingerprintResult` whose
 * `fingerprint_hash` is `null` when the input has no
 * recoverable error text.
 */
export function computeFingerprint(input: FingerprintInput): FingerprintResult {
  const norm = normaliseErrorText(input.message ?? '');
  const frames = extractTopStackFrames(input.stack, 3);
  const stepType = (input.stepType ?? '').trim().toLowerCase();
  if (!norm.text && frames.length === 0) {
    return {
      fingerprint_hash: null,
      normalised_message: '',
      top_stack_frames: [],
      normalisation_notes: norm.notes,
      version: ERROR_FINGERPRINT_VERSION,
    };
  }
  const fodder = [
    `v${ERROR_FINGERPRINT_VERSION}`,
    `step:${stepType}`,
    `msg:${norm.text}`,
    `frames:${frames.join('|')}`,
  ].join('\n');
  const hash = sha256Hex(fodder);
  return {
    fingerprint_hash: hash,
    normalised_message: norm.text,
    top_stack_frames: frames,
    normalisation_notes: norm.notes,
    version: ERROR_FINGERPRINT_VERSION,
  };
}

/**
 * Walk a `test_runs.step_results` JSONB array and emit one
 * fingerprint per failed step. Skips healed-and-recovered
 * steps. The caller upserts each result into
 * `error_fingerprints` + the join table.
 */
export function extractFingerprintsFromStepResults(
  stepResults: unknown,
): Array<FingerprintResult & { step_id: string | null; step_title: string | null }> {
  if (!Array.isArray(stepResults)) return [];
  const out: Array<FingerprintResult & { step_id: string | null; step_title: string | null }> = [];
  for (const raw of stepResults) {
    if (!raw || typeof raw !== 'object') continue;
    const step = raw as RawFailedStep & { stack?: string };
    if (step.status !== 'failed') continue;
    const fp = computeFingerprint({
      message: step.error ?? '',
      stack: step.errorStack ?? step.stack,
      stepType: step.stepType,
    });
    if (!fp.fingerprint_hash) continue;
    out.push({
      ...fp,
      step_id: step.stepId ?? null,
      step_title: step.stepTitle ?? null,
    });
  }
  return out;
}

/* -------- Wire schemas -------- */

export const ErrorFingerprintRowSchema = z.object({
  id: z.string().uuid(),
  org_id: z.string().uuid(),
  fingerprint_hash: z.string().length(64),
  normalised_message: z.string(),
  top_stack_frames: z.array(z.string()),
  step_type: z.string().nullable(),
  first_seen_at: z.string(),
  last_seen_at: z.string(),
  occurrence_count: z.number().int().nonnegative(),
  suite_count: z.number().int().nonnegative(),
  flow_count: z.number().int().nonnegative(),
  last_run_id: z.string().uuid().nullable(),
  first_run_id: z.string().uuid().nullable(),
  merged_into: z.string().uuid().nullable(),
  version: z.number().int().min(1),
});
export type ErrorFingerprintRow = z.infer<typeof ErrorFingerprintRowSchema>;

export const TOP_FINGERPRINTS_WINDOWS = ['7d', '30d', '90d'] as const;
export type TopFingerprintsWindow = (typeof TOP_FINGERPRINTS_WINDOWS)[number];
export const TopFingerprintsWindowSchema = z.enum(TOP_FINGERPRINTS_WINDOWS);

export const TopFingerprintEntrySchema = z.object({
  fingerprint_id: z.string().uuid(),
  fingerprint_hash: z.string().length(64),
  normalised_message: z.string(),
  step_type: z.string().nullable(),
  occurrence_count: z.number().int().nonnegative(),
  flow_count: z.number().int().nonnegative(),
  first_seen_at: z.string(),
  last_seen_at: z.string(),
  /**
   * Sparkline as `[date, count]` pairs (UTC days). Capped at
   * the window length so the chart can render edge-to-edge.
   */
  sparkline: z.array(z.tuple([z.string(), z.number().int().nonnegative()])),
});
export type TopFingerprintEntry = z.infer<typeof TopFingerprintEntrySchema>;

export const TopFingerprintsResponseSchema = z.object({
  window: TopFingerprintsWindowSchema,
  fingerprints: z.array(TopFingerprintEntrySchema),
});
export type TopFingerprintsResponse = z.infer<typeof TopFingerprintsResponseSchema>;

export const FingerprintDrawerOccurrenceSchema = z.object({
  run_id: z.string().uuid(),
  flow_id: z.string(),
  flow_name: z.string(),
  step_title: z.string().nullable(),
  failed_at: z.string(),
  environment: z.string(),
});
export type FingerprintDrawerOccurrence = z.infer<typeof FingerprintDrawerOccurrenceSchema>;

export const FingerprintDrawerResponseSchema = z.object({
  fingerprint: ErrorFingerprintRowSchema,
  sparkline: z.array(z.tuple([z.string(), z.number().int().nonnegative()])),
  occurrences: z.array(FingerprintDrawerOccurrenceSchema),
});
export type FingerprintDrawerResponse = z.infer<typeof FingerprintDrawerResponseSchema>;

export const MergeFingerprintRequestSchema = z.object({
  /** The fingerprint that should become the merge target. */
  into_fingerprint_id: z.string().uuid(),
});
export type MergeFingerprintRequest = z.infer<typeof MergeFingerprintRequestSchema>;

// ---------------------------------------------------------------------------
// `pending-test-observability-tier` v2 finishers.
// ---------------------------------------------------------------------------

/**
 * Outbound webhook payload fired on the FIRST occurrence of a new
 * error fingerprint. Versioned envelope so downstream consumers
 * can pin to a known shape — bumps land in `WEBHOOK_PAYLOAD_VERSION`.
 */
export const FIRST_SEEN_WEBHOOK_EVENT = 'observability.fingerprint.first_seen' as const;
export const FIRST_SEEN_WEBHOOK_VERSION = 1;

export const FirstSeenWebhookPayloadSchema = z.object({
  event: z.literal(FIRST_SEEN_WEBHOOK_EVENT),
  version: z.number().int().min(1),
  fired_at: z.string(),
  org_id: z.string().uuid(),
  fingerprint: z.object({
    id: z.string().uuid(),
    hash: z.string().length(64),
    normalised_message: z.string(),
    step_type: z.string().nullable(),
    top_stack_frames: z.array(z.string()),
    first_seen_at: z.string(),
    first_run_id: z.string().uuid().nullable(),
  }),
});
export type FirstSeenWebhookPayload = z.infer<typeof FirstSeenWebhookPayloadSchema>;

export function buildFirstSeenWebhookPayload(args: {
  orgId: string;
  firedAt?: Date;
  fingerprint: {
    id: string;
    hash: string;
    normalised_message: string;
    step_type: string | null;
    top_stack_frames: string[];
    first_seen_at: string;
    first_run_id: string | null;
  };
}): FirstSeenWebhookPayload {
  return {
    event: FIRST_SEEN_WEBHOOK_EVENT,
    version: FIRST_SEEN_WEBHOOK_VERSION,
    fired_at: (args.firedAt ?? new Date()).toISOString(),
    org_id: args.orgId,
    fingerprint: args.fingerprint,
  };
}

/* -------- Suite-comparison heatmap -------- */

export const SuiteHeatmapCellSchema = z.object({
  fingerprint_id: z.string().uuid(),
  project_id: z.string().uuid(),
  count: z.number().int().nonnegative(),
});
export type SuiteHeatmapCell = z.infer<typeof SuiteHeatmapCellSchema>;

export const SuiteHeatmapResponseSchema = z.object({
  window: TopFingerprintsWindowSchema,
  /**
   * Ordered top-N fingerprints (rows of the heatmap). The same
   * shape `getTopFingerprintsForOrg` returns, minus the sparkline.
   */
  fingerprints: z.array(
    z.object({
      fingerprint_id: z.string().uuid(),
      normalised_message: z.string(),
      step_type: z.string().nullable(),
      total_count: z.number().int().nonnegative(),
    }),
  ),
  /**
   * Ordered projects (columns of the heatmap). Display name +
   * total failures across the window.
   */
  projects: z.array(
    z.object({
      project_id: z.string().uuid(),
      project_name: z.string(),
      total_count: z.number().int().nonnegative(),
    }),
  ),
  /**
   * Sparse list of `(fingerprint, project) → count` cells. Sparse
   * (rather than a 2D array) so the JSON stays small for the
   * common case where most fingerprints hit one or two suites.
   */
  cells: z.array(SuiteHeatmapCellSchema),
});
export type SuiteHeatmapResponse = z.infer<typeof SuiteHeatmapResponseSchema>;

/* -------- Cross-org rollup (platform-admin) -------- */

export const CrossOrgRollupEntrySchema = z.object({
  org_id: z.string().uuid(),
  org_name: z.string(),
  total_failures_in_window: z.number().int().nonnegative(),
  unique_fingerprints_in_window: z.number().int().nonnegative(),
  /** Top 5 message previews so platform admins can scan at a glance. */
  top_messages: z.array(
    z.object({
      fingerprint_id: z.string().uuid(),
      normalised_message: z.string(),
      occurrence_count: z.number().int().nonnegative(),
    }),
  ),
});
export type CrossOrgRollupEntry = z.infer<typeof CrossOrgRollupEntrySchema>;

export const CrossOrgRollupResponseSchema = z.object({
  window: TopFingerprintsWindowSchema,
  generated_at: z.string(),
  total_orgs: z.number().int().nonnegative(),
  orgs: z.array(CrossOrgRollupEntrySchema),
});
export type CrossOrgRollupResponse = z.infer<typeof CrossOrgRollupResponseSchema>;

/* -------- AI remediation -------- */

export const FingerprintRemediationRequestSchema = z.object({
  /**
   * When true, callers explicitly ack that fingerprint text leaves
   * the network. Required even when `observability.aiRemediationEnabled`
   * is on org-side so a single curious operator can't trigger an
   * egress they didn't intend.
   */
  ack_egress: z.literal(true),
});
export type FingerprintRemediationRequest = z.infer<typeof FingerprintRemediationRequestSchema>;

export const FingerprintRemediationResponseSchema = z.object({
  ok: z.boolean(),
  fingerprint_id: z.string().uuid(),
  /**
   * Provider that served the call (`'mock'` when no AI creds are
   * configured — the deterministic fallback). UI surfaces this so
   * operators know whether they're looking at a real LLM response.
   */
  provider: z.enum(['openai', 'groq', 'anthropic', 'ollama', 'vllm', 'mock']),
  /** 1–2 sentence summary the operator sees as the card title. */
  summary: z.string(),
  /** Likely root-cause bullets (max 5). */
  likely_causes: z.array(z.string()).max(10),
  /** Suggested-fix steps (max 5). */
  suggested_fix_steps: z.array(z.string()).max(10),
  /**
   * Confidence band the LLM self-reported. Defaults to `'medium'`
   * when the LLM didn't surface a structured confidence.
   */
  confidence: z.enum(['low', 'medium', 'high']).default('medium'),
});
export type FingerprintRemediationResponse = z.infer<typeof FingerprintRemediationResponseSchema>;

/**
 * Pure: build the LLM prompt used by the remediation endpoint. The
 * prompt is intentionally compact + structured so cheap models
 * (e.g. Groq llama) produce useful JSON without long context.
 *
 * Kept in `@rynora/contracts` (not the API service) so the unit
 * tests can pin exact wording — small wording changes have real
 * downstream impact on the LLM's output shape.
 */
export function buildRemediationPrompt(args: {
  normalisedMessage: string;
  topStackFrames: string[];
  stepType: string | null;
  occurrenceCount: number;
  flowCount: number;
}): { system: string; user: string } {
  const system =
    'You are a senior QA engineer helping triage a recurring test failure. ' +
    'Respond with a JSON object matching this shape exactly: { "summary": string, "likely_causes": string[], "suggested_fix_steps": string[], "confidence": "low"|"medium"|"high" }. ' +
    'Be concrete: each suggested fix step should be actionable in 1 minute or less. ' +
    'Never include the raw error text in your output — the operator already has it.';
  const userParts: string[] = [];
  userParts.push(`Error (normalised): ${args.normalisedMessage || '(no message)'}`);
  if (args.stepType) userParts.push(`Step type: ${args.stepType}`);
  userParts.push(`Occurred ${args.occurrenceCount} time(s) across ${args.flowCount} flow(s).`);
  if (args.topStackFrames.length > 0) {
    userParts.push(`Top stack frames:\n${args.topStackFrames.slice(0, 3).join('\n')}`);
  }
  userParts.push('Return the JSON object only — no prose, no markdown fence.');
  return { system, user: userParts.join('\n\n') };
}

/**
 * Pure: best-effort parse an LLM response into a
 * `FingerprintRemediationResponse` body (minus the `ok` /
 * `fingerprint_id` / `provider` envelope, which the caller adds).
 * Returns `null` when the response isn't recoverable JSON.
 */
export function parseRemediationResponse(raw: string): {
  summary: string;
  likely_causes: string[];
  suggested_fix_steps: string[];
  confidence: 'low' | 'medium' | 'high';
} | null {
  if (!raw || typeof raw !== 'string') return null;
  let text = raw.trim();
  // Strip a leading markdown code fence if the model snuck one in.
  if (text.startsWith('```')) {
    text = text.replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/i, '');
  }
  // Some models prefix with "Here is the JSON:" — slice from the
  // first `{` to the last `}` and try again.
  const first = text.indexOf('{');
  const last = text.lastIndexOf('}');
  if (first > 0 && last > first) text = text.slice(first, last + 1);
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const o = parsed as Record<string, unknown>;
  const summary = typeof o.summary === 'string' ? o.summary.trim() : '';
  const likely = Array.isArray(o.likely_causes) ? (o.likely_causes as unknown[]) : [];
  const fix = Array.isArray(o.suggested_fix_steps) ? (o.suggested_fix_steps as unknown[]) : [];
  const conf = o.confidence;
  const confidence: 'low' | 'medium' | 'high' = conf === 'low' || conf === 'high' ? conf : 'medium';
  if (!summary) return null;
  return {
    summary,
    likely_causes: likely.filter((x): x is string => typeof x === 'string' && x.length > 0).slice(0, 10),
    suggested_fix_steps: fix.filter((x): x is string => typeof x === 'string' && x.length > 0).slice(0, 10),
    confidence,
  };
}

/* -------- Backfill -------- */

export const BackfillFingerprintsRequestSchema = z.object({
  /**
   * ISO timestamp. The aggregator's watermark for this org will be
   * pushed BACK to this value, then `aggregateOrgFingerprints` runs
   * with the supplied `limit`. Caller can call repeatedly with the
   * same `since` to chew through the backlog page-by-page.
   */
  since: z.string().datetime(),
  limit: z.number().int().min(1).max(5000).default(500),
});
export type BackfillFingerprintsRequest = z.infer<typeof BackfillFingerprintsRequestSchema>;

export const BackfillFingerprintsResponseSchema = z.object({
  runs_processed: z.number().int().nonnegative(),
  fingerprints_emitted: z.number().int().nonnegative(),
  watermark: z.string().nullable(),
  /** Set when the page hit the limit; clients should call again. */
  more_available: z.boolean(),
});
export type BackfillFingerprintsResponse = z.infer<typeof BackfillFingerprintsResponseSchema>;
